import { Project } from "../../project/types/project.type";
import { FileReq, SpendingDemandStatus, SpendingFile } from "../../spending/types/spending.types";
import { User } from "../../user/types/user.types";

export type SpendingDemandTypes = "PROJECT" | "BOX" | "CURRENT"

export type SpendingDemand = {
    spending_demand_id: string,
    spending_demand_readable_id?: string,
    spending_demand_payments: null,
    spending_demand_user: string,
    admin_account_id: string,
    spending_demand_category: string,
    spending_demand_supplier: string,
    spending_demand_project: string,
    spending_demand_chantier:string,
    spending_demand_chantier_part: null | string,
    spending_demand_equipement: string,
    spending_demand_equipement_part: string | null,
    spending_demand_amount: number,
    spending_demand_TVA: number,
    spending_demand_taxes: number,
    spending_demand_taxes_debit: number,
    spending_demand_amountTTC: number,
    spending_demand_total_payed: number,
    spending_demand_rest_to_pay: number,
    spending_demand_fully_paid: boolean,
    spending_demand_total_matched: number,
    spending_demand_rest_to_match: number,
    spending_demand_fully_matched: boolean,
    spending_demand_available: boolean,
    spending_demand_files: SpendingFile[],
    spending_demand_desc: string,
    spending_demand_date: string,
    spending_demand_update_date: string,
    spending_demand_status: SpendingDemandStatus,
    spending_demand_remarks:string,
    spending_demand_labels:string,
    spending_demand_files_update?: FileReq[],
    spending_demand_type: SpendingDemandTypes,
    spending_demand_box_association_id: string,
    type: string,
    is_to_process: boolean
    can_validate?: boolean
    manager_employee_id_waiting_for_validation: string[]
    manager_employee_id: string[]
    spending_demand_last_team_from?: string
    last_team_from_by_process_by?: { team_id: string }[]
    spending_demand_comptable_team?: string



}

export interface SpendingDemandProcessBy extends SpendingDemand {

}


export interface SpendingDemandExtended 
extends Omit<SpendingDemand, 'spending_demand_user' | 'spending_demand_project'>
{
    spending_demand_user: User
    spending_demand_project: Project | null
}


export interface FilterSpendingDemand {
    date?: {
      min?: string;
      max?: string;
    }
    status?: string[];
    amount?: {
      min?: number;
      max?: number;
    };

    amountTTC?:{
        min?: number;
        max?: number;
    }
    orderBy?: string;

    updateDate?:{
        min?: string;
        max?: string;
    }

    userId?: string[];

    toProcessStatus?: boolean;
    managerWaitingValidation?: string[];
    
    // Nouveaux filtres
    type?: SpendingDemandTypes[];
    projectId?: string[]; // Filtre par projet(s)
    boxAssociationId?: string[]; // Filtre par box association ID(s)
    boxId?: string; // Filtre texte Box ID (recherche partielle)
    readableId?: string; // Filtre par readable ID
    description?: string; // recherche plein-texte sur spending_demand_desc (param descSearch)
    whereQuery?: string; // Pour utiliser la syntaxe whereQuery de l'API
  }

/**
 * Interface pour les fichiers de demandes de dépenses (SPDF)
 * Représente la relation entre une demande de dépense et ses fichiers attachés
 */
export interface SPDF {
    spdf_id: string;
    spdf_spending_demand_id: string;
    spdf_file_id: string;
    spdf_created_at: string; //format : 2025-08-25T07:56:32.712003Z
    spdf_updated_at: string; //format : 2025-08-25T07:56:32.712003Z
    spdf_deleted_at: string;
    spdf_file_type: string; //format : 2025-08-25T07:56:32.712003Z
    spdf_file_name: string;
}

/**
 * Champs disponibles pour l'extract CSV des spending demands
 * Basé sur la documentation de l'API extract
 */
export type ExtractField = 
  | 'id'                    // Identifiant unique
  | 'readable_id'           // ID lisible
  | 'user'                  // Nom de l'utilisateur
  | 'admin'                 // ID de l'admin
  | 'category'              // ID de la catégorie
  | 'supplier'              // ID du fournisseur
  | 'project'               // Nom du projet
  | 'chantier'              // ID du chantier
  | 'box_id'                // ID de la box
  | 'amount'                // Montant HT
  | 'tva'                   // Montant TVA
  | 'taxes'                 // Montant taxes
  | 'taxes_debit'           // Montant taxes débit
  | 'amount_ttc'            // Montant TTC
  | 'total_payed'           // Montant total payé
  | 'rest_to_pay'          // Montant restant à payer
  | 'fully_paid'           // Entièrement payé
  | 'total_matched'        // Montant total matché
  | 'rest_to_match'        // Montant restant à matcher
  | 'fully_matched'        // Entièrement matché
  | 'available'            // Disponible pour matching
  | 'description'          // Description
  | 'date'                 // Date de création
  | 'update_date'          // Date de modification
  | 'status'               // Statut de la demande
  | 'type'                 // Type de demande (PROJECT, BOX, CURRENT)
  | 'credited_total'       // Montant crédité affiché dans le tableau
  | 'spending_total'       // Montant des dépenses affiché dans le tableau
  | 'remarks'              // Remarques
  | 'labels'               // Labels
  | 'type_cb'              // Type de carte bancaire
  | 'manager_assigned'     // IDs des managers assignés
  | 'manager_waiting'      // IDs des managers en attente
  | 'assignation_date'     // Date d'assignation
  | 'is_to_process'        // Si la demande est à traiter
  | 'team_to_process'      // Équipe responsable du traitement
  | 'last_team_from';      // Équipe d'origine ProBy

/**
 * Configuration des champs d'extract avec leurs descriptions
 */
export const EXTRACT_FIELDS_CONFIG: Record<ExtractField, { label: string; category: string; description: string }> = {
  // Champs de base
  id: { label: 'ID', category: 'Identité', description: 'Identifiant unique de la demande' },
  readable_id: { label: 'ID Lisible', category: 'Identité', description: 'ID lisible de la demande' },
  user: { label: 'Utilisateur', category: 'Identité', description: 'Nom de l\'utilisateur créateur' },
  admin: { label: 'Admin', category: 'Identité', description: 'ID de l\'administrateur' },
  
  // Champs de classification
  category: { label: 'Catégorie', category: 'Classification', description: 'ID de la catégorie' },
  supplier: { label: 'Fournisseur', category: 'Classification', description: 'ID du fournisseur' },
  project: { label: 'Projet', category: 'Classification', description: 'Nom du projet' },
  chantier: { label: 'Chantier', category: 'Classification', description: 'ID du chantier' },
  box_id: { label: 'Box ID', category: 'Classification', description: 'ID de la box' },
  
  // Champs financiers
  amount: { label: 'Montant HT', category: 'Financier', description: 'Montant hors taxes' },
  tva: { label: 'TVA', category: 'Financier', description: 'Montant de la TVA' },
  taxes: { label: 'Taxes', category: 'Financier', description: 'Montant des taxes' },
  taxes_debit: { label: 'Taxes Débit', category: 'Financier', description: 'Montant des taxes débit' },
  amount_ttc: { label: 'Montant TTC', category: 'Financier', description: 'Montant toutes taxes comprises' },
  total_payed: { label: 'Total Payé', category: 'Financier', description: 'Montant total payé' },
  rest_to_pay: { label: 'Reste à Payer', category: 'Financier', description: 'Montant restant à payer' },
  fully_paid: { label: 'Entièrement Payé', category: 'Financier', description: 'Entièrement payé' },
  total_matched: { label: 'Total Matché', category: 'Financier', description: 'Montant total matché' },
  rest_to_match: { label: 'Reste à Matcher', category: 'Financier', description: 'Montant restant à matcher' },
  fully_matched: { label: 'Entièrement Matché', category: 'Financier', description: 'Entièrement matché' },
  available: { label: 'Disponible', category: 'Financier', description: 'Disponible pour matching' },
  
  // Champs descriptifs
  description: { label: 'Description', category: 'Descriptif', description: 'Description de la demande' },
  type: { label: 'Type', category: 'Descriptif', description: 'Type de demande (Projet, Box, Courante)' },
  remarks: { label: 'Remarques', category: 'Descriptif', description: 'Remarques' },
  labels: { label: 'Labels', category: 'Descriptif', description: 'Labels associés' },
  type_cb: { label: 'Type CB', category: 'Descriptif', description: 'Type de carte bancaire' },
  
  // Champs temporels
  date: { label: 'Date', category: 'Temporel', description: 'Date de création' },
  update_date: { label: 'Date Modification', category: 'Temporel', description: 'Date de modification' },
  assignation_date: { label: 'Date Assignation', category: 'Temporel', description: 'Date d\'assignation' },
  
  // Champs de statut
  status: { label: 'Statut', category: 'Statut', description: 'Statut de la demande' },
  credited_total: { label: 'Montant crédité', category: 'Statut', description: 'Montant crédité affiché dans la liste manager' },
  spending_total: { label: 'Montant des dépenses', category: 'Statut', description: 'Montant total des dépenses affiché dans la liste manager' },
  is_to_process: { label: 'À Traiter', category: 'Statut', description: 'Si la demande est à traiter' },
  team_to_process: { label: 'Équipe à Traiter', category: 'Statut', description: 'Équipe responsable du traitement' },
  last_team_from: { label: "Équipe d'origine ProBy", category: 'Gestion', description: "Équipe d'origine ProBy" },
  
  // Champs de gestion des managers
  manager_assigned: { label: 'Manager Assigné', category: 'Gestion', description: 'IDs des managers assignés' },
  manager_waiting: { label: 'Manager En Attente', category: 'Gestion', description: 'IDs des managers en attente' }
};

/**
 * Champs par défaut recommandés pour l'extract
 */
export const DEFAULT_EXTRACT_FIELDS: ExtractField[] = [
  'id',
  'readable_id',
  'user', 
  'project',
  'type',
  'box_id',
  'credited_total',
  'spending_total',
  'amount_ttc',
  'status',
  'date',
  'description'
];

/**
 * Champs essentiels (toujours inclus)
 */
export const ESSENTIAL_EXTRACT_FIELDS: ExtractField[] = [
  'id',
  'readable_id',
  'status'
];

